import type { Vfs } from '../vfs/vfs';

export interface UserAccount {
  name: string;
  uid: number;
  gid: number;
  gecos: string;
  home: string;
  shell: string;
  groups: number[];
  /** Empreinte `sha256:…`, ou chaîne vide si aucun mot de passe. */
  passwordHash: string;
}

export interface GroupEntry {
  name: string;
  gid: number;
  members: string[];
}

/** Identité effective d'une session shell. */
export interface ShellIdentity {
  name: string;
  uid: number;
  gid: number;
  groups: number[];
  home: string;
}

const ROOT = 0;
const SHADOW_GID = 42;

/**
 * Transcrit un identifiant de compte web en nom d'utilisateur Debian valide.
 * Les identifiants acceptés (a-z, 0-9, point, tiret, underscore) passent tels
 * quels ; un identifiant commençant par un chiffre est préfixé de `u`.
 */
export function linuxUserName(accountId: string): string {
  let name = accountId
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9._-]/g, '-')
    .replace(/^[.-]+/, '');
  if (name === '') return 'eleve';
  if (!/^[a-z_]/.test(name)) name = `u${name}`;
  return name.slice(0, 32);
}

/** Base d'utilisateurs et de groupes simulée, synchronisée avec /etc. */
export class UserDatabase {
  private readonly usersByName = new Map<string, UserAccount>();
  private readonly usersByUid = new Map<number, UserAccount>();
  private readonly groupsByName = new Map<string, GroupEntry>();
  private readonly groupsByGid = new Map<number, GroupEntry>();

  static fromVfs(vfs: Vfs): UserDatabase {
    const database = new UserDatabase();
    database.loadFrom(vfs);
    return database;
  }

  /** Recharge entièrement la base depuis les fichiers /etc. */
  loadFrom(vfs: Vfs): void {
    this.usersByName.clear();
    this.usersByUid.clear();
    this.groupsByName.clear();
    this.groupsByGid.clear();

    for (const line of vfs.readFile('/etc/passwd', '/').split('\n')) {
      if (line === '' || line.startsWith('#')) continue;
      const [name, , uid, gid, gecos, home, shell] = line.split(':');
      if (!name) continue;
      this.addRawUser({
        name,
        uid: Number.parseInt(uid ?? '0', 10),
        gid: Number.parseInt(gid ?? '0', 10),
        gecos: gecos ?? '',
        home: home ?? '/',
        shell: shell ?? '/bin/bash',
        groups: [],
        passwordHash: '',
      });
    }

    for (const line of vfs.readFile('/etc/group', '/').split('\n')) {
      if (line === '' || line.startsWith('#')) continue;
      const [name, , gid, members] = line.split(':');
      if (!name) continue;
      const entry: GroupEntry = {
        name,
        gid: Number.parseInt(gid ?? '0', 10),
        members: (members ?? '').split(',').filter(Boolean),
      };
      this.groupsByName.set(name, entry);
      this.groupsByGid.set(entry.gid, entry);
    }

    try {
      for (const line of vfs.readFile('/etc/shadow', '/').split('\n')) {
        if (line === '' || line.startsWith('#')) continue;
        const [name, hash] = line.split(':');
        const user = name ? this.usersByName.get(name) : undefined;
        if (user && hash !== undefined && hash !== '') user.passwordHash = hash;
      }
    } catch {
      /* /etc/shadow absent : aucun mot de passe défini */
    }
  }

  byName(name: string): UserAccount | null {
    return this.usersByName.get(name) ?? null;
  }

  byUid(uid: number): UserAccount | null {
    return this.usersByUid.get(uid) ?? null;
  }

  groupByName(name: string): GroupEntry | null {
    return this.groupsByName.get(name) ?? null;
  }

  groupByGid(gid: number): GroupEntry | null {
    return this.groupsByGid.get(gid) ?? null;
  }

  nameOfUid(uid: number): string {
    return this.usersByUid.get(uid)?.name ?? String(uid);
  }

  nameOfGid(gid: number): string {
    return this.groupsByGid.get(gid)?.name ?? String(gid);
  }

  /** Groupes effectifs : groupe principal, appartenances listées et `groups` explicites. */
  shellGroups(account: UserAccount): number[] {
    const result = new Set<number>([account.gid, ...account.groups]);
    for (const group of this.groupsByName.values()) {
      if (group.members.includes(account.name)) result.add(group.gid);
    }
    return [...result].sort((a, b) => a - b);
  }

  identityOf(account: UserAccount): ShellIdentity {
    return {
      name: account.name,
      uid: account.uid,
      gid: account.gid,
      groups: this.shellGroups(account),
      home: account.home,
    };
  }

  addUser(name: string): UserAccount | null {
    if (this.usersByName.has(name)) return null;
    const uid = this.nextUid();
    const account: UserAccount = {
      name,
      uid,
      gid: uid,
      gecos: name,
      home: `/home/${name}`,
      shell: '/bin/bash',
      groups: [],
      passwordHash: '',
    };
    this.addRawUser(account);
    const group: GroupEntry = { name, gid: uid, members: [] };
    this.groupsByName.set(name, group);
    this.groupsByGid.set(uid, group);
    return account;
  }

  /**
   * Renomme un utilisateur en conservant uid, gid, foyer logique, gecos et
   * appartenances. Sert à aligner le compte Linux sur le compte web.
   */
  renameUser(oldName: string, newName: string): UserAccount | null {
    const previous = this.usersByName.get(oldName);
    if (!previous || oldName === newName || this.usersByName.has(newName)) return null;
    const memberships = [...this.groupsByName.values()]
      .filter((group) => group.members.includes(oldName))
      .map((group) => group.gid);

    this.removeUser(oldName);
    const account: UserAccount = { ...previous, name: newName, home: `/home/${newName}` };
    this.addRawUser(account);
    const group: GroupEntry = { name: newName, gid: previous.gid, members: [] };
    this.groupsByName.set(newName, group);
    this.groupsByGid.set(previous.gid, group);
    for (const gid of memberships) {
      const entry = this.groupsByGid.get(gid);
      if (entry && !entry.members.includes(newName)) entry.members.push(newName);
    }
    return account;
  }

  removeUser(name: string): boolean {
    const account = this.usersByName.get(name);
    if (!account) return false;
    this.usersByName.delete(name);
    this.usersByUid.delete(account.uid);
    const group = this.groupsByName.get(name);
    if (group) {
      this.groupsByName.delete(name);
      this.groupsByGid.delete(group.gid);
    }
    for (const entry of this.groupsByName.values()) {
      entry.members = entry.members.filter((member) => member !== name);
    }
    return true;
  }

  addUserToGroup(userName: string, groupName: string): boolean {
    const account = this.usersByName.get(userName);
    const group = this.groupsByName.get(groupName);
    if (!account || !group) return false;
    if (!group.members.includes(userName)) group.members.push(userName);
    return true;
  }

  setPassword(name: string, hash: string): boolean {
    const account = this.usersByName.get(name);
    if (!account) return false;
    account.passwordHash = hash;
    return true;
  }

  private addRawUser(account: UserAccount): void {
    this.usersByName.set(account.name, account);
    this.usersByUid.set(account.uid, account);
  }

  private nextUid(): number {
    let uid = 1000;
    while (this.usersByUid.has(uid)) uid += 1;
    return uid;
  }

  /** Réécrit /etc/passwd, /etc/group et /etc/shadow (en root). */
  writeToVfs(vfs: Vfs): void {
    const saved = vfs.actor;
    vfs.actor = { uid: ROOT, gid: ROOT, groups: [ROOT] };

    try {
      const users = [...this.usersByName.values()].sort((a, b) => a.uid - b.uid);
      vfs.writeFile(
        '/etc/passwd',
        '/',
        `${users.map((user) => `${user.name}:x:${user.uid}:${user.gid}:${user.gecos}:${user.home}:${user.shell}`).join('\n')}\n`,
        { owner: ROOT, group: ROOT, mode: 0o644 },
      );

      const groups = [...this.groupsByName.values()].sort((a, b) => a.gid - b.gid);
      vfs.writeFile(
        '/etc/group',
        '/',
        `${groups.map((group) => `${group.name}:x:${group.gid}:${group.members.join(',')}`).join('\n')}\n`,
        { owner: ROOT, group: ROOT, mode: 0o644 },
      );

      vfs.writeFile(
        '/etc/shadow',
        '/',
        `${users.map((user) => `${user.name}:${user.passwordHash}:19000:0:99999:7:::`).join('\n')}\n`,
        { owner: ROOT, group: SHADOW_GID, mode: 0o640 },
      );
    } finally {
      vfs.actor = saved;
    }
  }
}

export async function hashPassword(password: string): Promise<string> {
  const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(password));
  const hex = [...new Uint8Array(digest)]
    .map((byte) => byte.toString(16).padStart(2, '0'))
    .join('');
  return `sha256$${hex}`;
}

export async function verifyPassword(hash: string, password: string): Promise<boolean> {
  if (hash === '') return false;
  return (await hashPassword(password)) === hash;
}
